import { useState } from 'react';

import { SCOPE_HINTS, SCOPE_LABELS } from '../settings/settings.js';

import type { Session } from '../../core/auth/session.js';
import type { ApiScope } from '@nedelnik/shared';

/**
 * Экран согласия для моста агента (этап 15 плана).
 *
 * Сюда сервер приводит коннектор Claude с `GET /oauth/authorize`, проверив
 * клиента и адрес возврата. Человек уже вошёл (иначе `App` показал бы вход) и
 * решает одно: пускать ли этого агента и с какими правами. По «Разрешить»
 * сервер выписывает код, а коннектор обменивает его на обычный токен агента —
 * тот же, что в разделе «Агент» настроек, с именем клиента.
 *
 * Права — те же галочки, что при выпуске токена руками, с теми же
 * умолчаниями: коннектор просит «читать и писать», «удалять» человек
 * добавляет сам. Что просил клиент, показано, но не навязано.
 */

const ALL_SCOPES: readonly ApiScope[] = ['read', 'write', 'destroy'];

export interface AuthorizeRequest {
  client_id: string;
  client_name: string;
  redirect_uri: string;
  state?: string | undefined;
  code_challenge: string;
  scope: ApiScope[];
}

/** Разбор адреса `/authorize?…`, на который перенаправил сервер. */
export function parseAuthorizeRequest(search: string): AuthorizeRequest | undefined {
  const params = new URLSearchParams(search);
  const clientId = params.get('client_id');
  const redirectUri = params.get('redirect_uri');
  const challenge = params.get('code_challenge');
  if (clientId === null || redirectUri === null || challenge === null) return undefined;

  const scope = (params.get('scope') ?? 'read write')
    .split(/\s+/)
    .filter((item): item is ApiScope => ALL_SCOPES.includes(item as ApiScope));

  return {
    client_id: clientId,
    client_name: params.get('client_name') ?? 'Claude',
    redirect_uri: redirectUri,
    state: params.get('state') ?? undefined,
    code_challenge: challenge,
    scope: scope.length > 0 ? scope : ['read', 'write'],
  };
}

export function AuthorizeScreen({
  request,
  email,
  session,
  onRedirect,
}: {
  request: AuthorizeRequest;
  email: string | undefined;
  session: Pick<Session, 'authorizeAgent'>;
  /** Куда уйти после решения; в приложении это `location.assign`. */
  onRedirect: (url: string) => void;
}): React.ReactElement {
  const [scopes, setScopes] = useState<ApiScope[]>(request.scope);
  const [name, setName] = useState(request.client_name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const host = hostOf(request.redirect_uri);

  async function decide(decision: 'allow' | 'deny'): Promise<void> {
    if (decision === 'allow' && scopes.length === 0) {
      setError('Выберите хотя бы одно право — или нажмите «Не разрешать»');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const result = await session.authorizeAgent({
        client_id: request.client_id,
        redirect_uri: request.redirect_uri,
        state: request.state,
        code_challenge: request.code_challenge,
        scopes,
        name: name.trim().length > 0 ? name.trim() : request.client_name,
        decision,
      });
      onRedirect(result.redirect);
    } catch {
      setBusy(false);
      setError('Не вышло связаться с сервером — попробуйте ещё раз');
    }
  }

  return (
    <main className="shell">
      <section className="screen">
        <header className="screen__head">
          <h1>Разрешить доступ к задачам?</h1>
          <p className="head__sub">{email ?? 'учётная запись'}</p>
        </header>

        <section className="set">
          <p className="set__hint">
            <strong>{request.client_name}</strong> ({host}) просит доступ к вашим задачам. Он будет
            ходить от вашего имени и только в вашу учётную запись. Отозвать можно в любой момент в
            настройках, раздел «Агент».
          </p>

          <label className="field">
            <span className="field__label">Как назвать этот доступ</span>
            <input
              id="authorize-name"
              type="text"
              value={name}
              maxLength={64}
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <fieldset className="set__choices">
            <legend className="field__label">Что разрешено</legend>
            {ALL_SCOPES.map((scope) => (
              <label key={scope} className="set__choice">
                <input
                  id={`authorize-scope-${scope}`}
                  type="checkbox"
                  checked={scopes.includes(scope)}
                  onChange={(event) => {
                    setScopes(
                      event.target.checked
                        ? ALL_SCOPES.filter((item) => item === scope || scopes.includes(item))
                        : scopes.filter((item) => item !== scope),
                    );
                  }}
                />
                <span>
                  {SCOPE_LABELS[scope]}
                  <span className="set__sub">{SCOPE_HINTS[scope]}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="set__row-actions">
            <button type="button" disabled={busy} onClick={() => void decide('allow')}>
              Разрешить
            </button>
            <button
              type="button"
              className="link"
              disabled={busy}
              onClick={() => void decide('deny')}
            >
              Не разрешать
            </button>
          </div>

          {error !== undefined && (
            <p className="set__hint" role="status">
              {error}
            </p>
          )}
        </section>
      </section>
    </main>
  );
}

function hostOf(uri: string): string {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}
